import { usePageTitle } from '../hooks/usePageTitle';
import { useState } from 'react';

const PredictorPage = () => {
    usePageTitle('Predictor | Keliro');

    // --- CREDITS INITIALIZATION ---
    const getInitialCredits = () => {
        const storedCredits = localStorage.getItem('KeliroCredits');
        if (storedCredits === null) {
            localStorage.setItem('KeliroCredits', '1000');
            return 1000;
        }
        return parseInt(storedCredits, 10);
    };

    // --- STATES ---
    const [credits, setCredits] = useState(getInitialCredits());
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('currentUser')) || { name: 'Guest' });
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [bet, setBet] = useState(50);
    const [displayValue, setDisplayValue] = useState('?');
    const [isPlaying, setIsPlaying] = useState(false);
    const [result, setResult] = useState(null); // { won: bool, amount: number }
    const [history, setHistory] = useState(JSON.parse(localStorage.getItem('gameHistory')) || []);

    // --- LOGIC ---
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const handlePlay = async () => {
        if (selectedNumber === null) return alert('Please select a number first');
        if (bet < 10) return alert('Minimum bet is 10 credits');
        if (credits < bet) return alert('Not enough credits');

        setIsPlaying(true);
        setResult(null);

        const updatedCredits = credits - bet;
        updateGlobalCredits(updatedCredits);

        for (let i = 0; i < 15; i++) {
            setDisplayValue(Math.floor(Math.random() * 10));
            await sleep(100);
        }

        const finalNumber = Math.floor(Math.random() * 10);
        setDisplayValue(finalNumber);

        const won = finalNumber === selectedNumber;
        const winAmount = won ? bet * 9 : 0;
        const finalCredits = updatedCredits + winAmount;

        setResult({ won, amount: won ? winAmount : bet });
        
        updateGlobalCredits(finalCredits);
        addHistory('Predictor', bet, finalNumber, won);

        setIsPlaying(false);
    };

    const updateGlobalCredits = (newAmount) => {
        setCredits(newAmount);
        localStorage.setItem('KeliroCredits', newAmount.toString());

        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        if (currentUser) {
            currentUser.credits = newAmount;
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
            setUser(currentUser);
        }
    };

    const addHistory = (game, bet, result, won) => {
        const newEntry = { id: Date.now(), game, bet, result, won, date: new Date().toLocaleTimeString() };
        const updatedHistory = [newEntry, ...history].slice(0, 5); 
        setHistory(updatedHistory);
        localStorage.setItem('gameHistory', JSON.stringify(updatedHistory));
    };

    return (
        <main className="main">
            <div className="container">
                <section className="game-page">
                    <div className="game-container">
                        <div className="game-main">
                            <div className="game-header">
                                <div className="game-header-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 6v6l4 2" />
                                    </svg>
                                </div>
                                <h1 className="game-header-title">Predictor</h1>
                            </div>

                            <div className="game-area">
                                <div className={`predictor-display ${!isPlaying && result ? 'reveal' : ''}`}>
                                    {displayValue}
                                </div>

                                <p style={{ color: 'var(--text-medium)', marginBottom: '16px' }}>Select a number (0-9)</p>

                                <div className="number-grid">
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                                        <button
                                            key={num}
                                            className={`number-btn ${selectedNumber === num ? 'selected' : ''}`}
                                            onClick={() => !isPlaying && setSelectedNumber(num)}
                                            disabled={isPlaying}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>

                                <div className="game-controls">
                                    <div className="bet-input">
                                        <label>Bet:</label>
                                        <input
                                            type="number"
                                            value={bet}
                                            onChange={(e) => setBet(parseInt(e.target.value) || 0)}
                                            disabled={isPlaying}
                                            min="10"
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handlePlay}
                                        disabled={isPlaying}
                                    >
                                        {isPlaying ? 'Rolling...' : 'Reveal Number'}
                                    </button>
                                </div>

                                <div className="game-result">
                                    {result && (
                                        <>
                                            <div className="result-label">{result.won ? 'YOU WON!' : 'BETTER LUCK NEXT TIME'}</div>
                                            <div className={`result-value ${result.won ? 'win' : 'lose'}`}>
                                                {result.won ? `+${result.amount}` : `-${result.amount}`}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="panel mt-20">
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-medium)' }}>
                                    <strong>How to play:</strong> Select a number from 0-9, set your bet amount, and click
                                    "Reveal Number". If your number matches the revealed number, you win 9x your bet!
                                </p>
                            </div>
                        </div>

                        <div className="game-sidebar">
                            <div className="sidebar-card">
                                <h3>Your Credits</h3>
                                <div className="credits-display">{credits.toLocaleString()}</div>
                            </div>

                            <div className="sidebar-card">
                                <h3>Recent Plays</h3>
                                <div className="history-list">
                                    {history.length === 0 ? (
                                        <p style={{ color: 'var(--text-light)', fontSize: '13px' }}>No plays yet</p>
                                    ) : (
                                        history.map(item => (
                                            <div key={item.id} className="history-item" style={{ fontSize: '12px', marginBottom: '5px', borderBottom: '1px solid #333' }}>
                                                <span>{item.date}</span> | <span>Result: {item.result}</span> |
                                                <span style={{ color: item.won ? '#4caf50' : '#f44336' }}> {item.won ? 'Win' : 'Loss'}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="sidebar-card">
                                <h3>Win Multiplier</h3>
                                <p style={{ fontSize: '14px', color: 'var(--text-medium)' }}>
                                    Match the number: <strong style={{ color: 'var(--primary)' }}>9x</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default PredictorPage;